<template>
    <div class="competition-settings">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <div ref="messages">
                <panel-info-box v-if="successMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ successMessage }}</b>
                    </p>
                </panel-info-box>
                <panel-info-box v-if="errorMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ errorMessage }}</b>
                    </p>
                </panel-info-box>
            </div>

            <panel-competition-numbering-form
                v-model="form"
                :competition="competition"
                :saving="saving"
                :assigning="assigning"
                @save="saveSettings"
                @assign="startAssign"
            />
        </template>

        <modal ref="previewModal" size="xl" body-class="h-auto" @modal-close="onPreviewClose">
            <template #header>
                <h3 class="preview-modal__title">Podgląd numeracji</h3>
            </template>
            <template #body>
                <div v-if="simulating" class="preview-modal__loading">Trwa symulacja numeracji…</div>
                <template v-else-if="simulation">
                    <div v-if="simulation.errors && simulation.errors.length" class="preview-modal__errors">
                        <p v-for="(err, i) in simulation.errors" :key="`e-${i}`">{{ err }}</p>
                    </div>
                    <div v-if="simulation.warnings && simulation.warnings.length" class="preview-modal__warnings">
                        <p v-for="(warn, i) in simulation.warnings" :key="`w-${i}`">{{ warn }}</p>
                    </div>
                    <p class="preview-modal__summary">
                        Przypisań: {{ simulation.numbers ? simulation.numbers.length : 0 }}
                        <span v-if="simulation.noProblems"> — brak problemów</span>
                    </p>
                    <div class="preview-modal__table-wrap">
                        <table class="preview-modal__table">
                            <thead>
                                <tr>
                                    <th>Zawodnik</th>
                                    <th>Klasyfikacja</th>
                                    <th>Obecny</th>
                                    <th>Nowy</th>
                                    <th>Typ</th>
                                    <th>Uwagi</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in simulation.numbers || []" :key="row.playerId">
                                    <td>{{ playerLabel(row) }}</td>
                                    <td>{{ row.classificationName }}</td>
                                    <td>{{ formatNumber(row.currentNumber) }}</td>
                                    <td>{{ formatNumber(row.assignedNumber) }}</td>
                                    <td>{{ typeLabel(row.type) }}</td>
                                    <td>{{ rowNotes(row) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </template>
            <template #footer>
                <div class="preview-modal__footer">
                    <base-button btn2 :disabled="confirming" @click="closePreview">Anuluj</base-button>
                    <base-button
                        btn1
                        :loading="confirming"
                        :disabled="confirming || !canConfirmAssign"
                        @click="confirmAssign"
                    >
                        Potwierdź nadanie
                    </base-button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import PanelCompetitionNumberingForm from '@/components/PanelCompetitionNumberingForm';
    import PanelInfoBox from '@/components/PanelInfoBox';
    import Modal from '@/components/Modal';
    import { getNumberingSettings, updateNumberingSettings, simulateNumbering, assignNumbering } from '@/api/numbering';
    import { READ_COMPETITION_CLASSIFICATIONS } from '@/store/actions.type';

    const TYPE_LABELS = {
        new: 'nowy',
        keep: 'zachowany',
        prev: 'z cyklu',
        skip: 'pominięty',
    };

    function emptyForm() {
        return {
            autoNumbers: 'manual',
            autoNumbersEmail: false,
            numbersOnlyPayed: 'all',
            numbersUsePrevious: 'new',
            numbersOnlyPrevious: false,
            numbersOrder: 'date',
            numbersKeep: 'keep',
            classifications: [],
        };
    }

    export default {
        name: 'PanelCompetitionSettingNumbers',
        components: {
            PanelCompetitionNumberingForm,
            PanelInfoBox,
            Modal,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                saving: false,
                assigning: false,
                simulating: false,
                confirming: false,
                form: emptyForm(),
                simulation: null,
                pendingClassificationIds: null,
                successMessage: '',
                errorMessage: '',
            };
        },
        computed: {
            ...mapGetters(['classificationsPlayerCount']),
            competitionId() {
                return this.competition.id;
            },
            canConfirmAssign() {
                if (!this.simulation || !this.simulation.hash) {
                    return false;
                }
                const errors = this.simulation.errors || [];
                return errors.length === 0;
            },
        },
        async created() {
            await this.loadSettings();
            this.ready = true;
        },
        methods: {
            ...mapActions([READ_COMPETITION_CLASSIFICATIONS]),
            typeLabel(type) {
                return TYPE_LABELS[type] || type || '—';
            },
            playerLabel(row) {
                const name = [row.lastname, row.firstname].filter(Boolean).join(' ').trim();
                return name || `#${row.playerId}`;
            },
            formatNumber(value) {
                if (value == null || value === '' || value === 0) {
                    return '—';
                }
                return value;
            },
            rowNotes(row) {
                return [...(row.errors || []), ...(row.warnings || [])].join(' ');
            },
            classificationNameById(id) {
                const match = (this.form.classifications || []).find((item) => String(item.id) === String(id));
                return match ? match.name : id;
            },
            stripHtml(value) {
                return String(value)
                    .replace(/<br\s*\/?>/gi, ' ')
                    .replace(/<[^>]+>/g, '')
                    .trim();
            },
            normalizeNumbers(numbers) {
                if (!numbers) {
                    return [];
                }
                const list = Array.isArray(numbers) ? numbers : Object.values(numbers);
                return list.map((item) => {
                    const { classificationId, assignedNumber, currentNumber } = item;
                    const assigned = assignedNumber !== undefined ? assignedNumber : item.new;
                    const current = currentNumber !== undefined ? currentNumber : item.current;
                    return {
                        playerId: item.playerId,
                        firstname: item.firstname || '',
                        lastname: item.lastname || '',
                        classificationId,
                        classificationName:
                            item.classificationName || this.classificationNameById(classificationId) || '',
                        currentNumber: current,
                        assignedNumber: assigned,
                        type: item.type,
                        errors: item.errors || [],
                        warnings: item.warnings || [],
                    };
                });
            },
            normalizeWarnings(warnings) {
                if (!warnings) {
                    return [];
                }
                if (Array.isArray(warnings)) {
                    return warnings;
                }
                return Object.keys(warnings).map((id) => {
                    const name = this.classificationNameById(id);
                    const text = this.stripHtml(warnings[id]);
                    return name ? `${name}: ${text}` : text;
                });
            },
            normalizeSimulation(data) {
                const source = (data && data.numeration) || data || {};
                const numbers = this.normalizeNumbers(source.numbers);
                const errors = Array.isArray(source.errors) ? source.errors.slice() : [];
                numbers.forEach((row) => {
                    (row.errors || []).forEach((err) => {
                        if (err) {
                            errors.push(err);
                        }
                    });
                });
                return {
                    noProblems: Boolean(source.noProblems),
                    errors,
                    warnings: this.normalizeWarnings(source.warnings),
                    numbers,
                    hash: source.hash || null,
                    alert: (data && data.alert) || '',
                };
            },
            scrollToMessages() {
                this.$nextTick(() => {
                    const el = this.$refs.messages;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        return;
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
            },
            mapSettingsToForm(data) {
                const settings = (data && data.competitionSettings) || data || {};
                const classifications = (data && data.classifications) || [];
                return {
                    autoNumbers:
                        settings.autoNumbers === true || settings.autoNumbers === 1 || settings.autoNumbers === '1'
                            ? 'auto'
                            : 'manual',
                    autoNumbersEmail: Boolean(settings.autoNumbersEmail),
                    numbersOnlyPayed:
                        settings.numbersOnlyPayed === true ||
                        settings.numbersOnlyPayed === 1 ||
                        settings.numbersOnlyPayed === '1'
                            ? 'paid'
                            : 'all',
                    numbersUsePrevious:
                        settings.numbersUsePrevious === true ||
                        settings.numbersUsePrevious === 1 ||
                        settings.numbersUsePrevious === '1'
                            ? 'previous'
                            : 'new',
                    numbersOnlyPrevious: Boolean(settings.numbersOnlyPrevious),
                    numbersOrder: settings.numbersOrder === 'alphabetic' ? 'alphabetic' : 'date',
                    numbersKeep:
                        settings.numbersKeep === false || settings.numbersKeep === 0 || settings.numbersKeep === '0'
                            ? 'reassign'
                            : 'keep',
                    classifications: classifications.map((item) => {
                        const min = Number(item.numbersMin) || 0;
                        const max = Number(item.numbersMax) || 0;
                        const playersTotal = item.playersTotal != null ? item.playersTotal : 0;
                        const count = this.classificationsPlayerCount[item.id];
                        const playersPayCount =
                            count && count.playersPayCount != null ? Number(count.playersPayCount) : playersTotal;
                        return {
                            id: item.id,
                            name: item.name,
                            playersTotal,
                            playersPayCount,
                            playersNumbered: item.playersNumbered != null ? item.playersNumbered : 0,
                            playersLimit: item.playersLimit != null ? item.playersLimit : item.limit || 0,
                            hasNumbers: Boolean(item.hasNumbers),
                            enabled: min !== 0 || max !== 0,
                            numbersMin: min,
                            numbersMax: max,
                        };
                    }),
                };
            },
            buildPayload() {
                return {
                    autoNumbers: this.form.autoNumbers === 'auto',
                    autoNumbersEmail: Boolean(this.form.autoNumbersEmail),
                    numbersOnlyPayed: this.form.numbersOnlyPayed === 'paid',
                    numbersUsePrevious: this.form.numbersUsePrevious === 'previous',
                    numbersOnlyPrevious: Boolean(this.form.numbersOnlyPrevious),
                    numbersOrder: this.form.numbersOrder === 'alphabetic' ? 'alphabetic' : 'date',
                    numbersKeep: this.form.numbersKeep === 'keep',
                    classifications: (this.form.classifications || []).map((row) => ({
                        id: row.id,
                        numbersMin: row.enabled ? Number(row.numbersMin) || 0 : 0,
                        numbersMax: row.enabled ? Number(row.numbersMax) || 0 : 0,
                    })),
                };
            },
            enabledClassificationIds() {
                return (this.form.classifications || []).filter((row) => row.enabled).map((row) => row.id);
            },
            normalizeError(e) {
                if (!e) {
                    return 'Wystąpił błąd';
                }
                if (typeof e === 'string') {
                    return e;
                }
                if (e.error) {
                    return e.error;
                }
                if (e.message) {
                    return e.message;
                }
                return 'Wystąpił błąd';
            },
            async loadSettings() {
                this.errorMessage = '';
                try {
                    const [data] = await Promise.all([
                        getNumberingSettings(this.competitionId),
                        this.READ_COMPETITION_CLASSIFICATIONS(this.competitionId),
                    ]);
                    this.form = this.mapSettingsToForm(data);
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                    this.form = emptyForm();
                }
            },
            async saveSettings() {
                this.saving = true;
                this.successMessage = '';
                this.errorMessage = '';
                try {
                    await updateNumberingSettings(this.competitionId, this.buildPayload());
                    this.successMessage = 'Ustawienia numeracji zostały zapisane';
                    await this.loadSettings();
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                } finally {
                    this.saving = false;
                    this.scrollToMessages();
                }
            },
            async startAssign() {
                this.assigning = true;
                this.simulating = true;
                this.successMessage = '';
                this.errorMessage = '';
                this.simulation = null;
                this.pendingClassificationIds = this.enabledClassificationIds();
                this.$refs.previewModal.openModal();

                try {
                    await updateNumberingSettings(this.competitionId, this.buildPayload());
                    const body = {};
                    if (this.pendingClassificationIds.length) {
                        body.classificationIds = this.pendingClassificationIds;
                    }
                    const data = await simulateNumbering(this.competitionId, body);
                    this.simulation = this.normalizeSimulation(data);
                } catch (e) {
                    this.simulation = {
                        noProblems: false,
                        errors: [this.normalizeError(e)],
                        warnings: [],
                        numbers: [],
                        hash: null,
                    };
                } finally {
                    this.simulating = false;
                    this.assigning = false;
                }
            },
            async confirmAssign() {
                if (!this.canConfirmAssign) {
                    return;
                }
                this.confirming = true;
                this.errorMessage = '';
                try {
                    const payload = { hash: this.simulation.hash };
                    if (this.pendingClassificationIds && this.pendingClassificationIds.length) {
                        payload.classificationIds = this.pendingClassificationIds;
                    }
                    const result = await assignNumbering(this.competitionId, payload);
                    const count = result && result.count != null ? result.count : null;
                    this.successMessage =
                        count != null
                            ? `Nadano numery (${count}).`
                            : (result && result.message) || (result && result.alert) || 'Numery zostały nadane';
                    this.closePreview();
                    await this.loadSettings();
                    this.scrollToMessages();
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                    if (this.simulation) {
                        this.simulation = {
                            ...this.simulation,
                            errors: [this.normalizeError(e), ...(this.simulation.errors || [])],
                        };
                    }
                    this.scrollToMessages();
                } finally {
                    this.confirming = false;
                }
            },
            closePreview() {
                if (this.$refs.previewModal) {
                    this.$refs.previewModal.closeModal();
                }
                this.onPreviewClose();
            },
            onPreviewClose() {
                this.simulation = null;
                this.pendingClassificationIds = null;
                this.simulating = false;
            },
        },
    };
</script>

<style scoped>
    .competition-settings {
        width: 100%;
    }
    @media (min-width: 992px) {
        .competition-settings {
            margin-left: 30px;
        }
    }
    .preview-modal__title {
        margin: 0;
        font-size: 18px;
    }
    .preview-modal__loading {
        padding: 24px 0;
        text-align: center;
    }
    .preview-modal__errors {
        background: #f8d7da;
        color: #721c24;
        border-radius: 4px;
        padding: 12px 16px;
    }
    .preview-modal__warnings {
        background: #fff3cd;
        color: #856404;
        border-radius: 4px;
        padding: 12px 16px;
    }
    .preview-modal__summary {
        margin: 16px 0;
        padding: 0 16px;
        font-weight: 700;
    }
    .preview-modal__table-wrap {
        max-height: 360px;
        overflow: auto;
        border: 1px solid #cfd8dc;
        border-radius: 4px;
    }
    .preview-modal__table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    .preview-modal__table th,
    .preview-modal__table td {
        padding: 8px 10px;
        text-align: left;
        border-bottom: 1px solid #eceff1;
    }
    .preview-modal__table th {
        position: sticky;
        top: 0;
        background: #eceff1;
        font-weight: 700;
    }
    .preview-modal__footer {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        justify-content: flex-end;
        width: 100%;
    }
</style>
